'use client';

import { type RefObject, useEffect } from 'react';

const DEFAULT_MAX_WAIT_MS = 1500;
const DEFAULT_RETRY_DELAY_MS = 50;
const DEFAULT_TOLERANCE_PX = 2;

interface ScrollMetrics {
  currentY: number;
  maxY: number;
}

interface MarketingPageScrollRestorationAdapter {
  getMetrics: () => ScrollMetrics;
  scrollTo: (targetY: number) => void;
  requestFrame: (callback: FrameRequestCallback) => unknown;
  cancelFrame: (handle: unknown) => void;
  setDelay: (callback: () => void, delayMs: number) => unknown;
  clearDelay: (handle: unknown) => void;
  observeSize: (callback: () => void) => () => void;
  subscribeToUserInteraction: (callback: () => void) => () => void;
  useManualScrollRestoration: () => () => void;
}

interface CreateMarketingPageScrollRestorationOptions {
  targetY: number;
  adapter: MarketingPageScrollRestorationAdapter;
  maxWaitMs?: number;
  retryDelayMs?: number;
  tolerancePx?: number;
}

interface UseMarketingPageScrollRestorationOptions {
  enabled: boolean;
  targetY?: number;
  rootRef: RefObject<HTMLElement | null>;
}

export function createMarketingPageScrollRestoration({
  targetY,
  adapter,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  tolerancePx = DEFAULT_TOLERANCE_PX,
}: CreateMarketingPageScrollRestorationOptions) {
  let isStopped = false;
  let frameHandle: unknown;
  let retryHandle: unknown;
  let deadlineHandle: unknown;

  const stopObservingSize = adapter.observeSize(scheduleAttempt);
  const unsubscribeFromUserInteraction = adapter.subscribeToUserInteraction(stop);
  const restoreNativeScrollRestoration = adapter.useManualScrollRestoration();

  function clearScheduledWork() {
    if (frameHandle !== undefined) {
      adapter.cancelFrame(frameHandle);
      frameHandle = undefined;
    }
    if (retryHandle !== undefined) {
      adapter.clearDelay(retryHandle);
      retryHandle = undefined;
    }
  }

  function stop() {
    if (isStopped) return;

    isStopped = true;
    clearScheduledWork();
    if (deadlineHandle !== undefined) {
      adapter.clearDelay(deadlineHandle);
      deadlineHandle = undefined;
    }
    stopObservingSize();
    unsubscribeFromUserInteraction();
    restoreNativeScrollRestoration();
  }

  function scheduleRetry() {
    if (isStopped) return;

    retryHandle = adapter.setDelay(() => {
      retryHandle = undefined;
      scheduleAttempt();
    }, retryDelayMs);
  }

  function verifyRestoredPosition() {
    if (isStopped) return;

    const { currentY } = adapter.getMetrics();
    if (Math.abs(currentY - targetY) <= tolerancePx) {
      stop();
      return;
    }

    scheduleRetry();
  }

  function attemptRestore() {
    if (isStopped) return;

    const { currentY, maxY } = adapter.getMetrics();
    if (Math.abs(currentY - targetY) <= tolerancePx) {
      stop();
      return;
    }
    if (targetY > maxY + tolerancePx) {
      scheduleRetry();
      return;
    }

    adapter.scrollTo(targetY);
    frameHandle = adapter.requestFrame(() => {
      frameHandle = undefined;
      verifyRestoredPosition();
    });
  }

  function scheduleAttempt() {
    if (isStopped) return;

    clearScheduledWork();
    // 连续两帧让缓存中的展开态、Tab 和商品列表先提交到 DOM，再读取可滚动高度。
    frameHandle = adapter.requestFrame(() => {
      frameHandle = adapter.requestFrame(() => {
        frameHandle = undefined;
        attemptRestore();
      });
    });
  }

  deadlineHandle = adapter.setDelay(stop, maxWaitMs);
  scheduleAttempt();

  return stop;
}

function createBrowserAdapter(rootElement: HTMLElement): MarketingPageScrollRestorationAdapter {
  return {
    getMetrics: () => ({
      currentY: window.pageYOffset || document.documentElement.scrollTop || 0,
      maxY: Math.max(0, document.documentElement.scrollHeight - window.innerHeight),
    }),
    scrollTo: (targetY) => {
      window.scrollTo({ top: targetY, behavior: 'auto' });
    },
    requestFrame: (callback) => window.requestAnimationFrame(callback),
    cancelFrame: (handle) => window.cancelAnimationFrame(handle as number),
    setDelay: (callback, delayMs) => window.setTimeout(callback, delayMs),
    clearDelay: (handle) => window.clearTimeout(handle as number),
    observeSize: (callback) => {
      if (typeof ResizeObserver === 'undefined') {
        return () => undefined;
      }

      const observer = new ResizeObserver(callback);
      observer.observe(rootElement);
      return () => observer.disconnect();
    },
    subscribeToUserInteraction: (callback) => {
      window.addEventListener('wheel', callback, { passive: true });
      window.addEventListener('touchstart', callback, { passive: true });
      window.addEventListener('pointerdown', callback, { passive: true });
      window.addEventListener('keydown', callback);

      return () => {
        window.removeEventListener('wheel', callback);
        window.removeEventListener('touchstart', callback);
        window.removeEventListener('pointerdown', callback);
        window.removeEventListener('keydown', callback);
      };
    },
    useManualScrollRestoration: () => {
      if (!('scrollRestoration' in window.history)) {
        return () => undefined;
      }

      const previousScrollRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = previousScrollRestoration;
      };
    },
  };
}

export function useMarketingPageScrollRestoration({
  enabled,
  targetY,
  rootRef,
}: UseMarketingPageScrollRestorationOptions) {
  useEffect(() => {
    const rootElement = rootRef.current;
    if (
      !enabled ||
      !rootElement ||
      typeof targetY !== 'number' ||
      !Number.isFinite(targetY) ||
      targetY < 0
    ) {
      return;
    }

    return createMarketingPageScrollRestoration({
      targetY,
      adapter: createBrowserAdapter(rootElement),
    });
  }, [enabled, rootRef, targetY]);
}
